/**
 * icons.ts — Registro seguro de ícones do Studio
 *
 * O documento informa APENAS uma chave (`iconName`). O desenho do ícone é do
 * sistema, escrito aqui, igual no editor, na página publicada, no PDF e no
 * e-mail. Nenhum SVG, caminho ou endereço vindo do usuário é aceito, o que
 * elimina a superfície de injeção por imagem vetorial.
 *
 * RELEASE: v1.0.0 - 2026-09-11 - Versão inicial (vocabulário editorial, Fase 3)
 */

/** Traçados de cada ícone (todos em uma caixa de 24×24, traço de 1.6). */
const TRACADOS: Record<string, { label: string; d: string[] }> = {
  shield: { label: 'Escudo', d: ['M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z'] },
  check: { label: 'Confirmação', d: ['M20 7L10 17l-6-6'] },
  clock: { label: 'Relógio', d: ['M12 21a9 9 0 100-18 9 9 0 000 18z', 'M12 7v5l3 2'] },
  phone: { label: 'Telefone', d: ['M5 4h4l2 5-2.5 1.5a12 12 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2z'] },
  pin: { label: 'Localização', d: ['M12 21s7-5.4 7-11a7 7 0 10-14 0c0 5.6 7 11 7 11z', 'M12 12a2.5 2.5 0 100-5 2.5 2.5 0 000 5z'] },
  car: { label: 'Veículo', d: ['M4 16v3h3v-3M17 16v3h3v-3', 'M3 16h18v-4l-2-5H5L3 12v4z', 'M7 16h.01M17 16h.01'] },
  truck: { label: 'Caminhão', d: ['M3 7h11v9H3z', 'M14 10h4l3 3v3h-7z', 'M7 19a2 2 0 100-4 2 2 0 000 4zM18 19a2 2 0 100-4 2 2 0 000 4z'] },
  wrench: { label: 'Manutenção', d: ['M15 4a5 5 0 00-4.6 7L4 17.4 6.6 20l6.4-6.4A5 5 0 1015 4z'] },
  star: { label: 'Estrela', d: ['M12 4l2.5 5.2 5.5.8-4 3.9 1 5.6-5-2.7-5 2.7 1-5.6-4-3.9 5.5-.8L12 4z'] },
  lock: { label: 'Cadeado', d: ['M6 11h12v9H6z', 'M9 11V8a3 3 0 016 0v3'] },
  headset: { label: 'Atendimento', d: ['M4 14v-2a8 8 0 0116 0v2', 'M4 14h3v5H5a1 1 0 01-1-1v-4zM20 14h-3v5h2a1 1 0 001-1v-4z'] },
  bolt: { label: 'Agilidade', d: ['M13 3L5 14h6l-1 7 8-11h-6l1-7z'] },
  chart: { label: 'Resultados', d: ['M4 20h16', 'M7 20v-6M12 20V7M17 20v-9'] },
  users: { label: 'Pessoas', d: ['M8 11a3 3 0 100-6 3 3 0 000 6z', 'M2 20v-1c0-2.2 2.7-4 6-4s6 1.8 6 4v1', 'M16 6a3 3 0 010 6', 'M17 15c2.4.4 4 1.8 4 3.5V20'] },
  document: { label: 'Documento', d: ['M7 3h7l4 4v14H7z', 'M14 3v4h4', 'M10 12h6M10 16h6'] },
  coin: { label: 'Financeiro', d: ['M12 21a9 9 0 100-18 9 9 0 000 18z', 'M9.5 9.5A2.5 2.5 0 0112 8c1.4 0 2.5.9 2.5 2M14.5 14.5A2.5 2.5 0 0112 16c-1.4 0-2.5-.9-2.5-2', 'M12 6.5v11'] },
};

/** Lista de chaves válidas. */
export const STUDIO_ICON_NAMES = Object.keys(TRACADOS);

/** Opções prontas para o painel de propriedades e para o catálogo. */
export const STUDIO_ICON_OPTIONS = [
  { value: '', label: 'Sem ícone' },
  ...STUDIO_ICON_NAMES.map((chave) => ({ value: chave, label: TRACADOS[chave].label })),
];

/** Diz se a chave existe no registro. */
export function iconeExiste(nome: unknown): boolean {
  return typeof nome === 'string' && Object.prototype.hasOwnProperty.call(TRACADOS, nome);
}

/** Traçados do ícone (usados pelo desenho React). */
export function tracadosDoIcone(nome: unknown): string[] {
  return iconeExiste(nome) ? TRACADOS[nome as string].d : [];
}

/**
 * SVG pronto do ícone, para o compilador HTML.
 * Chave desconhecida devolve texto vazio: nunca desenha nada arbitrário.
 */
export function studioIconSvg(nome: unknown, tamanho = 22): string {
  const tracados = tracadosDoIcone(nome);
  if (!tracados.length) return '';
  const lado = Number.isFinite(tamanho) ? Math.min(Math.max(Number(tamanho), 12), 64) : 22;
  const caminhos = tracados.map((d) => `<path d="${d}" />`).join('');
  return `<svg class="st-icon" width="${lado}" height="${lado}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${caminhos}</svg>`;
}
